import React, { useContext } from 'react';
import memoize from 'memoize-async';
import {
	ApButtonSecondary,
	ApLoaderDefault,
} from '@alixpartners/ui-components';

import {
	getInitatives,
	getWaves,
	createWave,
	deleteWave,
	getInitative,
	updateInitiative,
	assignInitiativeToWave,
} from 'utils/apiRequests';
import { QueryStringContext } from 'contexts/QueryStringContext';
import { PermissionsContext } from 'contexts/PermissionsContext';
import { SettingsContext } from 'contexts/SettingsContext';
import { ApiDataContext } from 'contexts/ApiDataContext';
import { SettingsOptionsContext } from 'contexts/SettingsOptionsContext';
import { getCurrencySymbolFromOptions } from 'Details/utils';
import { ConfirmationDialog } from 'TaxonomyMapper/ConfirmationDialog';
import InitiativesWavesList from './InitiativesWavesList';
import SecondaryNav from './SecondaryNav';
import { getFormattedCurrencyWithIdealDenomination } from 'utils/number';

import {
	Wrapper,
	LoadingContainer,
} from './InitiativesToWavesAllocation.styles';

const memoizedApi = {
	getWaves: memoize(getWaves),
	getInitatives: memoize(getInitatives),
	getInitative: memoize(getInitative),
	updateInitiative: memoize(updateInitiative),
};

export class InitiativesToWavesAllocationContainer extends React.Component {
	state = {
		confirmationDialogData: null,
		isLoading: true,
		deleteWaveId: null,
	};

	async componentDidMount() {
		await this.fetchData();
	}

	fetchData = async () => {
		const { apiCacheTrigger } = this.props;

		const waves = await memoizedApi.getWaves(apiCacheTrigger);
		let initiatives = await memoizedApi.getInitatives({}, apiCacheTrigger);

		this.setData(initiatives, waves);
	};

	assignInitiativeToWave = async (initiative, wave) => {
		this.setState({ isUpdating: true });

		await assignInitiativeToWave([
			{
				initiativeId: initiative.id,
				waveId: wave.id,
			},
		]);

		this.refreshData();
	};

	refreshData = async () => {
		const { clearApiCache } = this.props;

		clearApiCache();

		await this.fetchData();

		this.setState({ isUpdating: false });
	};

	createWave = async () => {
		this.setState({ isUpdating: true });

		try {
			await createWave();

			this.refreshData();
		} catch (e) {
			this.setState({ isUpdating: false });
		}
	};

	deleteWave = async id => {
		this.setState({ isUpdating: true });
		this.hideConfirmation();

		await deleteWave(id);

		this.refreshData();
	};

	componentDidUpdate(prevProps) {
		const { hideAllocatedInitiatives } = this.props;
		const { originalInitiativesData, waves } = this.state;

		if (hideAllocatedInitiatives !== prevProps.hideAllocatedInitiatives) {
			this.setData(originalInitiativesData, waves);
		}
	}

	setData = (initiatives, waves) => {
		const { hideAllocatedInitiatives } = this.props;

		let filteredInitiativesList = initiatives;

		if (hideAllocatedInitiatives) {
			filteredInitiativesList = filteredInitiativesList.filter(
				item => !item.waveId
			);
		}

		const transformedInitiatives = this.getTransformedInitiativesData(
			filteredInitiativesList,
			waves
		);

		const wavesCount = initiatives.reduce((acc, initiative) => {
			if (initiative.waveId) {
				const res = [...acc];

				res[initiative.waveId] += 1;

				return res;
			}

			return acc;
		}, Array(waves.length + 1).fill(0));

		this.setState({
			originalInitiativesData: initiatives,
			initiatives: transformedInitiatives,
			waves,
			wavesCount,
			isLoading: false,
		});
	};

	getTransformedInitiativesData(initiatives) {
		return initiatives.map(
			({ id, name, type, high, low, spendType, waveId }) => {
				const result = {
					id,
					name,
					type,
					spendType,
					low: this.getFormattedPrice(low),
					high: this.getFormattedPrice(high),
					waveId,
				};

				return result;
			}
		);
	}

	getFormattedPrice = value => {
		const { currencySymbol } = this.props;

		let formattedPrice = getFormattedCurrencyWithIdealDenomination({
			value,
			isUnitSign: true,
			hideDecimals: false,
		});

		formattedPrice = formattedPrice
			? `${currencySymbol}${formattedPrice}`
			: formattedPrice;

		return formattedPrice;
	};

	showConfirmation = wave => {
		this.setState({
			confirmationDialogData: {
				id: wave.id,
				message:
					'You are about to delete a wave. Please note that all initiatives will be removed and will no longer be allocated to a wave.',
			},
		});
	};

	hideConfirmation = () => {
		this.setState({ confirmationDialogData: null });
	};

	onHideAllocatedInitiativesChange = () => {
		const { hideAllocatedInitiatives, setQueryStringStateAndPush } = this.props;

		setQueryStringStateAndPush({
			hideAllocatedInitiatives: !hideAllocatedInitiatives,
		});
	};

	render() {
		const {
			isLoading,
			isUpdating,
			initiatives,
			waves,
			wavesCount,
			originalInitiativesData,
			confirmationDialogData,
		} = this.state;
		const { hideAllocatedInitiatives, currencySymbol } = this.props;

		if (isLoading) {
			return (
				<Wrapper>
					<LoadingContainer>
						<ApLoaderDefault />
					</LoadingContainer>
				</Wrapper>
			);
		}

		return (
			<Wrapper isUpdating={isUpdating}>
				<SecondaryNav
					waves={waves}
					hideAllocatedInitiatives={hideAllocatedInitiatives}
					onHideAllocatedInitiativesChange={
						this.onHideAllocatedInitiativesChange
					}
					createWave={this.createWave}
				/>

				<InitiativesWavesList
					initiatives={initiatives}
					waves={waves}
					wavesCount={wavesCount}
					initiativesCount={originalInitiativesData.length}
					currencySymbol={currencySymbol}
					assignInitiativeToWave={this.assignInitiativeToWave}
					deleteWave={this.showConfirmation}
				/>

				{confirmationDialogData && (
					<ConfirmationDialog
						title="Delete wave"
						text={confirmationDialogData.message}
						onClose={this.hideConfirmation}
						suppressCheckbox
						confirmButton={
							<ApButtonSecondary
								onClick={() => this.deleteWave(confirmationDialogData.id)}
							>
								Delete
							</ApButtonSecondary>
						}
					/>
				)}
			</Wrapper>
		);
	}
}

export default React.memo(props => {
	const {
		currency,
		showMappedItems,
		setQueryStringStateAndPush,
		hideAllocatedInitiatives,
	} = useContext(QueryStringContext);

	const { permissions } = useContext(PermissionsContext);
	const { companiesList } = useContext(SettingsContext);
	const { clearApiCache, apiCacheTrigger } = useContext(ApiDataContext);
	const {
		options: { currency: currencyList },
	} = useContext(SettingsOptionsContext);

	const currencySymbol = getCurrencySymbolFromOptions(currencyList, currency);

	return (
		<InitiativesToWavesAllocationContainer
			{...props}
			permissions={permissions || []}
			showMappedItems={!!showMappedItems}
			setQueryStringStateAndPush={setQueryStringStateAndPush}
			currencySymbol={currencySymbol}
			currency={currency}
			companiesList={companiesList}
			clearApiCache={clearApiCache}
			apiCacheTrigger={apiCacheTrigger}
			hideAllocatedInitiatives={hideAllocatedInitiatives === 'true'}
		/>
	);
});
